import React, { useState } from "react";
import { toast } from "sonner";
import { Headphones, Radio, Rss, Play, Podcast } from "lucide-react";

/**
 * PodcastPlatformStrip
 * ---------------------
 * Renders below the article's inline audio player when the piece has
 * been mirrored to Transistor.fm (i.e. `article.transistorShareUrl`
 * exists). The Transistor share URL is a landing page that already
 * routes listeners to Apple / Spotify / Overcast / Pocket Casts, so
 * all "Listen on X" buttons deep-link to that share URL. When we
 * eventually store platform-specific show URLs (Apple / Spotify),
 * those override the share URL for the corresponding buttons.
 *
 * The row also includes a "Copy RSS" button so power users can plug
 * the feed into any podcast client of their choice.
 */

const FEED_URL = "https://feeds.transistor.fm/vexent-wire";

const PlatformBtn = ({ href, label, Icon, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    data-testid={testid}
    className="group flex items-center gap-2 px-3 py-2 border border-neutral-300 bg-white hover:bg-[#0B1F33] hover:text-[#FFFFFF] transition-colors"
  >
    <Icon className="w-4 h-4" strokeWidth={2} />
    <span className="text-[10.5px] uppercase tracking-[0.2em] font-condensed font-bold">
      {label}
    </span>
  </a>
);

const PodcastPlatformStrip = ({ article }) => {
  const [copied, setCopied] = useState(false);
  const share = article?.transistorShareUrl;
  if (!share) return null;

  const copyFeed = async () => {
    try {
      await navigator.clipboard.writeText(FEED_URL);
      setCopied(true);
      toast.success("RSS feed copied", {
        description: "Paste into any podcast player to subscribe.",
      });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy — long-press the RSS link to share instead.");
    }
  };

  return (
    <section
      data-testid="podcast-platform-strip"
      className="mt-4 mb-8 border-l-4 border-[#B8E9F1] pl-4"
    >
      <div className="flex flex-col sm:flex-row items-start gap-4">
        <img
          src="/assets/vexent-podcast-cover.png"
          alt="Vexent Wire podcast cover art"
          width={80}
          height={80}
          className="w-20 h-20 shrink-0 border border-neutral-300 shadow-sm object-cover"
          loading="lazy"
        />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.28em] uppercase font-condensed font-bold text-[#0891B2] mb-1">
            The Vexent Wire Podcast
          </div>
          <div className="font-serif-display text-[17px] font-bold text-[#0B1F33] leading-tight">
            Vexent Wire
          </div>
          <p className="mt-1 text-[12px] text-neutral-600 leading-snug">
            News, insights, perspective — beyond the headlines. New episodes with each research piece.
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PlatformBtn
          testid="listen-apple"
          href={share}
          label="Apple Podcasts"
          Icon={Headphones}
        />
        <PlatformBtn
          testid="listen-spotify"
          href={share}
          label="Spotify"
          Icon={Play}
        />
        <PlatformBtn
          testid="listen-overcast"
          href={share}
          label="Overcast"
          Icon={Radio}
        />
        <PlatformBtn
          testid="listen-pocketcasts"
          href={share}
          label="Pocket Casts"
          Icon={Podcast}
        />
        <button
          type="button"
          onClick={copyFeed}
          data-testid="listen-rss-copy"
          className="flex items-center gap-2 px-3 py-2 border border-neutral-300 bg-white hover:bg-[#B8E9F1] hover:text-[#0B1F33] transition-colors"
          aria-label="Copy RSS feed URL"
        >
          <Rss className="w-4 h-4" strokeWidth={2} />
          <span className="text-[10.5px] uppercase tracking-[0.2em] font-condensed font-bold">
            {copied ? "Copied" : "Copy RSS"}
          </span>
        </button>
      </div>
      <p className="mt-2 text-[11px] text-neutral-500 leading-snug max-w-[520px]">
        Feed URL: <code className="text-[10px] bg-[#FFFFFF] px-1.5 py-0.5">{FEED_URL}</code>
      </p>
    </section>
  );
};

export default PodcastPlatformStrip;
